import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Banknote, Coins, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg, imgSrc, money, startCheckout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SendMoneyDialog } from "@/components/MoneyDialogs";

function Recipients() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/wallet/recipients", { params: { q } }).then((r) => setList(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="card-lux p-6" data-testid="wallet-send-section">
      <p className="eyebrow mb-4">Send cash {user.membership !== "vip" && "· VIP only"}</p>
      <div className="relative mb-4"><Search className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
        <Input data-testid="recipient-search-input" placeholder="Search members" value={q} onChange={(e) => setQ(e.target.value)} className="bg-zinc-900 pl-9" /></div>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {list.map((m) => (
          <SendMoneyDialog key={m.id} to={m} trigger={
            <button data-testid={`recipient-${m.id}`} className="flex w-full items-center gap-3 rounded p-2 text-left hover:bg-white/5">
              <img src={imgSrc(m.photo)} alt="" className="h-9 w-9 rounded-full object-cover" /><span className="text-sm">{m.name}</span><span className="ml-auto text-xs text-red-400">Send →</span>
            </button>} />
        ))}
      </div>
    </div>
  );
}

function PayoutDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: "", method: "Bank transfer", details: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/wallet/payout", { amount_cents: Math.round(Number(f.amount) * 100), method: f.method, details: f.details });
      toast.success("Payout requested — pending admin approval"); setOpen(false); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="wallet-payout-btn" className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm hover:bg-white hover:text-black"><Banknote className="h-4 w-4" />Cash out</button></DialogTrigger>
      <DialogContent data-testid="payout-dialog" className="border-zinc-800 bg-zinc-950">
        <DialogHeader><DialogTitle className="font-display text-3xl">Request a payout</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Input data-testid="payout-amount-input" type="number" min="10" step="0.01" required placeholder="Amount (min $10)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-zinc-900" />
          <select data-testid="payout-method-select" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className="w-full rounded border border-zinc-800 bg-zinc-900 p-2.5 text-sm">
            {["Bank transfer", "Cash App", "PayPal", "Zelle"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <Input data-testid="payout-details-input" required placeholder="Account details ($cashtag, email or bank info)" value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} className="bg-zinc-900" />
          <p className="text-xs text-zinc-500">Requires ID verification. Payouts are reviewed and sent by our team.</p>
          <button data-testid="payout-submit-btn" className="btn-red w-full rounded-full py-3 font-semibold">Request payout</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ t }) => (
  <div className="flex items-center gap-4 border-b border-white/5 py-3 text-sm" data-testid="ledger-row">
    {t.amount_cents < 0 || t.tokens < 0 ? <ArrowUpRight className="h-4 w-4 text-red-500" /> : <ArrowDownLeft className="h-4 w-4 text-emerald-400" />}
    <div className="flex-1"><p>{t.note}</p><p className="text-xs text-zinc-600">{new Date(t.created_at).toLocaleString()}</p></div>
    <span className="font-mono-x text-xs">{t.tokens ? `${t.tokens > 0 ? "+" : ""}${t.tokens} tk ` : ""}{t.amount_cents ? money(t.amount_cents).replace("$-", "-$") : ""}</span>
  </div>
);

export default function WalletPage() {
  const { refresh } = useAuth();
  const [w, setW] = useState(null);
  const load = () => { api.get("/wallet").then((r) => setW(r.data)); refresh(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const cashout = async () => {
    try { const { data } = await api.post("/wallet/cashout-tokens"); toast.success(`${money(data.credited_cents)} added to wallet`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const topup = (k) => startCheckout(k).catch((e) => toast.error(errMsg(e)));
  if (!w) return null;
  return (
    <main className="mx-auto max-w-7xl px-5 py-12 lg:px-8" data-testid="wallet-page">
      <p className="eyebrow mb-3">Cash exchange</p>
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="card-lux relative overflow-hidden p-10">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-red-600/20 blur-3xl" />
          <p className="eyebrow">Wallet balance</p>
          <p data-testid="wallet-balance" className="font-display mt-2 text-7xl sm:text-8xl">{money(w.wallet_cents)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[["wallet_2500", 25], ["wallet_5000", 50], ["wallet_10000", 100]].map(([k, a]) => (
              <button key={k} data-testid={`wallet-topup-${a}`} onClick={() => topup(k)} className="btn-red flex items-center gap-1 rounded-full px-5 py-3 text-sm font-semibold"><Plus className="h-4 w-4" />${a}</button>
            ))}
            <PayoutDialog onDone={load} />
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-white/5 pt-6 text-sm">
            <span className="flex items-center gap-1 text-amber-300"><Coins className="h-4 w-4" />{w.tokens} tokens to spend</span>
            <span data-testid="earned-tokens" className="text-zinc-400">{w.earned_tokens} tips earned (≈ {money(w.earned_tokens * w.token_cashout_cents)})</span>
            {w.earned_tokens > 0 && <button data-testid="cashout-tokens-btn" onClick={cashout} className="text-red-400 hover:text-white">Convert tips to cash →</button>}
          </div>
        </div>
        <Recipients />
      </div>
      <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div><p className="eyebrow mb-3">Activity</p>{w.ledger.length === 0 ? <p className="text-sm text-zinc-600">No activity yet.</p> : w.ledger.map((t) => <Row key={t.id} t={t} />)}</div>
        <div><p className="eyebrow mb-3">Payouts</p>{w.payouts.length === 0 ? <p className="text-sm text-zinc-600">No payouts yet.</p> : w.payouts.map((p) => (
          <div key={p.id} data-testid="payout-row" className="flex justify-between border-b border-white/5 py-3 text-sm"><span>{p.method} · {money(p.amount_cents)}</span><span className={`uppercase tracking-widest text-xs ${p.status === "paid" ? "text-emerald-400" : p.status === "rejected" ? "text-red-400" : "text-amber-300"}`}>{p.status}</span></div>
        ))}</div>
      </div>
    </main>
  );
}
